import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import {
    CepConstants,
    SgVehicleChangeType,
    SgVehicleForm,
    SgVehicleRegistrationForm
} from 'saga-cep-specific-components';
import { useSgTrackPageName, SgVehicleLookupUtil } from 'saga-modules';
import { EndorsementService } from 'gw-capability-policychange';
import { useValidation } from 'gw-portals-validation-react';
import _ from 'lodash';

import './SgEndorsementVehiclePage.messages';

import { Loader } from '@jutro/components';
import { WizardPage } from 'gw-portals-wizard-react';
import { events } from 'gw-platform-events-js';
import CommonConstants from 'sg-common-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import metadata from './SgEndorsementVehiclePage.metadata.json5';

const VehicleChangeTypes = {
    VEHICLE_CHANGE: 'VEHICLE_CHANGE',
    VEHICLE_EDIT: 'VEHICLE_EDIT',
    REG_NUMBER_CHANGE: 'REG_NUMBER_CHANGE'
};

function SgEndorsementVehiclePage(props) {
    const {
        id,
        wizardData: modelVM,
        updateWizardData: updateData,
        goNext,
        sgWizardPropsExt
    } = props;

    const {
        handleCancel,
        handleGoBack
    } = sgWizardPropsExt;

    const {
        onValidate,
        isComponentValid,
    } = useValidation(id);

    const [isLoading, setLoadingState] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [activeChangeType, setActiveChangeType] = useState('');
    const [isChangingReg, setIsChangingReg] = useState(false);
    const [isChangingVehicle, setIsChangingVehicle] = useState(false);
    const [isEditingVehicle, setIsEditingVehicle] = useState(false);
    const originalPolicyChangeData = useRef(_.cloneDeep(modelVM.value));

    const commonProps = {
        onValidate,
        modelVM,
        showErrors,
        setShowErrors,
        updateData
    };

    useSgTrackPageName(CepConstants.PAGE_NAMES.VEHICLE_CHANGES);

    const restoreModel = useCallback(() => {
        modelVM.value = _.cloneDeep(originalPolicyChangeData.current);
        updateData(modelVM);
    }, [originalPolicyChangeData, modelVM]);

    const onRegistrationChange = useCallback(() => {
        if (isChangingReg) {
            return;
        }
        if (isEditingVehicle || isChangingVehicle) {
            setIsEditingVehicle(false);
            setIsChangingVehicle(false);
            restoreModel();
        }
        modelVM.lobData.privateMotor.coverables.spmVehicle.registrationNumber.value = '';
        updateData(modelVM);
        setIsChangingReg(true);
    }, [isChangingReg, modelVM, updateData, isEditingVehicle, isChangingVehicle]);

    const onEditVehicle = useCallback(() => {
        if (isEditingVehicle) {
            return;
        }
        if (isChangingReg || isChangingVehicle) {
            setIsChangingReg(false);
            setIsChangingVehicle(false);
            restoreModel();
        }
        setIsEditingVehicle(true);
    }, [modelVM, isChangingReg, setIsChangingReg, isChangingVehicle, setIsChangingVehicle]);

    const onChangeVehicle = useCallback(() => {
        if (isChangingVehicle) {
            return;
        }
        if (isEditingVehicle || isChangingReg) {
            setIsEditingVehicle(false);
            setIsChangingReg(false);
            restoreModel();
        }
        SgVehicleLookupUtil.clearVehicleProperties(modelVM.lobData.privateMotor.coverables.spmVehicle, [
            'registrationNumber', 'year', 'make', 'model', 'bodyType', 'engineCapacity', 'transmission',
            'numberOfDoors', 'description', 'abiNumber', 'vehicleRatingGroup', 'motorhomeEngineCapacity',
            'hasExperianValue', 'vehicleValue', 'isTrackerInstalled', 'isLeaseInNameOfPhOrSpouse', 'hasModifications',
            'vehicleUse', 'mileage', 'vehicleParkingType', 'yearPurchased', 'vehicleType', 'vehicleOwnerType'
        ]);
        setIsChangingVehicle(true);
    }, [modelVM, isChangingVehicle, isEditingVehicle, isChangingReg]);

    useEffect(() => {
        setShowErrors(false);
        switch (activeChangeType) {
            case VehicleChangeTypes.REG_NUMBER_CHANGE:
                onRegistrationChange();
                break;
            case VehicleChangeTypes.VEHICLE_EDIT:
                onEditVehicle();
                break;
            case VehicleChangeTypes.VEHICLE_CHANGE:
                onChangeVehicle();
                break;
            default:
        }
    }, [activeChangeType]);

    const onContinue = useCallback(() => {
        if (!isComponentValid) {
            setShowErrors(true);
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return;
        }
        setLoadingState(true);
        EndorsementService.saveEndorsement(modelVM.value)
            .then((response) => {
                const policyChangeValueCopy = _.cloneDeep(modelVM.value);
                _.merge(policyChangeValueCopy, response);
                modelVM.value = policyChangeValueCopy;
                updateData(modelVM);
                goNext(modelVM);
            }).finally(() => {
                setLoadingState(false);
            });
    }, [isComponentValid, modelVM, updateData, goNext]);

    const overrideProps = {
        cepEndorsementVehicleTypeOfChange: {
            activeChangeType,
            setActiveChangeType
        },
        cepEndorsementVehicleChange: {
            visible: isChangingVehicle,
            isChangingVehicle: true,
            ...commonProps
        },
        cepEndorsementVehicleReg: {
            visible: isChangingReg,
            ...commonProps
        },
        cepEndorsementVehicleEdit: {
            visible: isEditingVehicle,
            isChangingVehicle: false,
            ...commonProps
        },
        continuePolicyChangeButton: {
            visible: !!activeChangeType,
            onClick: onContinue
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleCancel,
            handleGoBack,
            onContinue
        },
        resolveComponentMap: {
            SgVehicleChangeType,
            SgVehicleForm,
            SgVehicleRegistrationForm,
            SgCepSideMenu
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgEndorsementVehiclePage;
